import { VoteIcon, TrendingUpIcon, UsersIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Button from '@/components/form/Button';
import { ArrowRightIcon } from 'lucide-react';

const steps = [
  {
    icon: <VoteIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />,
    title: "Create Your SNAP",
    description: "Ask a question that matters to you"
  },
  {
    icon: <UsersIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />,
    title: "Engage Your Audience",
    description: "Share with your community"
  },
  {
    icon: <TrendingUpIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />,
    title: "Watch It Grow",
    description: "See real-time results and insights"
  }
];

export default function HowItWorks() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate('/create');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">How it Works</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold text-gray-900 sm:text-4xl">
              Three Simple Steps
            </p>
            <p className="mt-4 max-w-2xl text-lg text-gray-500 lg:mx-auto">
              Get started with SnapVote in minutes and begin making better decisions together.
            </p>
          </motion.div>
        </div>

        <div className="mt-10 sm:mt-12">
          <dl className="space-y-6 sm:space-y-4 md:space-y-0 md:grid md:grid-cols-3 md:gap-6 lg:gap-8">
            {steps.map((step, index) => (
              <motion.div 
                key={step.title}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="bg-white rounded-lg p-6 h-full shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-md">
                        {step.icon}
                      </div>
                    </div>
                    <div className="ml-4">
                      <dt className="text-base sm:text-lg font-semibold text-gray-900">
                        {step.title}
                      </dt>
                      <dd className="mt-1 text-sm sm:text-base text-gray-500">
                        {step.description}
                      </dd>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </dl>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Button
            onClick={handleGetStarted}
            variant="primary"
            className="px-8 py-3 md:py-4 md:px-10 text-base md:text-lg"
          >
            Create SNAP
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
}